import { on, reducer } from "ts-action";
import {
  clearServiceAuditState,
  commitServiceAuditState,
  removeScheduledServiceDisable,
  setAuditActor,
  setAuditPrevMenuCategory,
  setAuditPrevMenuItem,
  setScheduledServiceDisable,
  setServiceAuditBaseline,
  setServiceDisableMetadata,
  setServiceEnableOverride,
} from "../actions/auditActions";
import {
  BranchSelectionMap,
  IScheduledServiceDisable,
  IServiceDisableMetadata,
  ServiceType,
} from "../../types/audit";

export interface IServiceAuditReducerState {
  baselineSelection: BranchSelectionMap | null;
  disableMetadata: Record<string, Partial<Record<ServiceType, IServiceDisableMetadata>>>;
  scheduledDisables: IScheduledServiceDisable[];
  enableOverrides: Record<string, boolean>;
  auditActor: { id?: string; name: string; email?: string; user_name?: string; actor_type?: string } | null;
  auditPrevMenuCategory: Record<string, any> | null;
  auditPrevMenuItem: Record<string, any> | null;
}

const emptySelection: BranchSelectionMap = {
  pickup_branch_ids: {},
  delivery_branch_ids: {},
  drive_thru_branch_ids: {},
};

const initialState: IServiceAuditReducerState = {
  baselineSelection: null,
  disableMetadata: {},
  scheduledDisables: [],
  enableOverrides: {},
  auditActor: null,
  auditPrevMenuCategory: null,
  auditPrevMenuItem: null,
};

const scheduledKey = (branchId: string, serviceType: ServiceType) =>
  `${branchId}:${serviceType}`;

export const serviceAuditReducer = reducer<IServiceAuditReducerState>(
  [
    on(setServiceAuditBaseline, (state, { payload }): IServiceAuditReducerState => ({
      ...state,
      baselineSelection: payload,
    })),
    on(setServiceDisableMetadata, (state, { payload }): IServiceAuditReducerState => {
      const { branchId, serviceType, metadata } = payload;
      const branchMeta = { ...(state.disableMetadata[branchId] || {}) };
      const enableOverrides = { ...state.enableOverrides };

      if (metadata) {
        branchMeta[serviceType] = metadata;
        delete enableOverrides[scheduledKey(branchId, serviceType)];
      } else {
        delete branchMeta[serviceType];
      }

      return {
        ...state,
        disableMetadata: {
          ...state.disableMetadata,
          [branchId]: branchMeta,
        },
        enableOverrides,
      };
    }),
    on(setAuditActor, (state, { payload }): IServiceAuditReducerState => ({
      ...state,
      auditActor: payload,
    })),
    on(setScheduledServiceDisable, (state, { payload }): IServiceAuditReducerState => {
      if (!payload) {
        return state;
      }

      const key = scheduledKey(payload.branchId, payload.serviceType);
      const scheduledDisables = state.scheduledDisables.filter(
        (item) => scheduledKey(item.branchId, item.serviceType) !== key
      );

      return {
        ...state,
        scheduledDisables: [...scheduledDisables, payload],
      };
    }),
    on(removeScheduledServiceDisable, (state, { payload }): IServiceAuditReducerState => ({
      ...state,
      scheduledDisables: state.scheduledDisables.filter(
        (item) =>
          !(
            item.branchId === payload.branchId &&
            item.serviceType === payload.serviceType
          )
      ),
    })),
    on(setServiceEnableOverride, (state, { payload }): IServiceAuditReducerState => ({
      ...state,
      enableOverrides: {
        ...state.enableOverrides,
        [scheduledKey(payload.branchId, payload.serviceType)]: true,
      },
    })),
    on(setAuditPrevMenuCategory, (state, { payload }): IServiceAuditReducerState => ({
      ...state,
      auditPrevMenuCategory: payload,
    })),
    on(setAuditPrevMenuItem, (state, { payload }): IServiceAuditReducerState => ({
      ...state,
      auditPrevMenuItem: payload,
    })),
    on(commitServiceAuditState, (state, { payload }): IServiceAuditReducerState => ({
      ...state,
      baselineSelection: payload,
      disableMetadata: {},
      scheduledDisables: [],
      enableOverrides: {},
    })),
    on(clearServiceAuditState, (): IServiceAuditReducerState => ({
      ...initialState,
      baselineSelection: emptySelection,
    })),
  ],
  initialState
);